import {
  CheckCircleFilled,
  CloseCircleFilled,
  InboxOutlined,
  LoadingOutlined,
  PictureOutlined,
} from "@ant-design/icons"
import * as Sentry from "@sentry/react"
import { App, Button, DatePicker, Modal, Select, Tooltip, Upload } from "antd"
import { type UploadChangeParam } from "antd/es/upload/interface"
import { Dayjs } from "dayjs"
import { UploadTaskSnapshot } from "firebase/storage"
import { map } from "lodash"
import { useEffect, useMemo, useRef, useState } from "react"

import { curriculumAtoms, projectAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import TextOverrun from "#blocks/TextOverrun"
import Firebase from "#classes/Firebase"
import Firestore from "#classes/Firestore"
import { CLASS_GROUPS } from "#constants/classOptions"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { CurriculumConstructor } from "#customTypes/composedTypes/curriculumTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { UploadStatus } from "#customTypes/composedTypes/firestoreTypes"
import { ProjectConstructor } from "#customTypes/composedTypes/projectTypes"
import { StudentArtwork } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { invariant } from "#customTypes/types"
import { useOneDoc, useSomeDocs } from "#hooks/jotai/useSubscription"
import { invalidateRenditionUrl } from "#hooks/newHooks/useRenditionUrl"
import {
  buildArtworkFileName,
  createArtworkFileId,
} from "#utils/artworkFileNames"
import {
  getRenditionPath,
  isRenditionWritable,
  parseStoragePathFromUrl,
  RENDITION_TIER,
} from "#utils/artworkRenditionHelpers"
import { getNow, STANDARD_DATE_FORMAT } from "#utils/dateHelpers"
import { convertHeicToJpeg } from "#utils/imageHelpers"
import { uploadArtworkRenditions } from "#utils/imageRenditions"

import {
  ARTWORK_UPLOAD_PROCESSING_ERROR,
  artworkUploadFailureMessage,
  filenameUploadFailedToast,
  HeicConversionFailure,
  isHeicConversionFailure,
  reportHeicConversionFailure,
} from "./artworkUploadErrors"
import {
  getArtworkDateFields,
  getDefaultUploadClassId,
  sortClassesByRecency,
} from "./artworkUploadHelpers"

import type { UploadFile, UploadProps } from "antd"

interface ArtworkUploadModalProps {
  open: boolean
  onClose: () => void
  studentId: Uuid
  /** Shown in the title so a drop on a crowded roster is easy to verify. */
  studentName?: string
  classes: ClassConstructor[]
  initialClassId?: Uuid
  /**
   * Files staged by a drag-and-drop target. When present the Dragger is
   * replaced by the staged list and nothing uploads until the user confirms
   * the class/date and clicks Upload.
   */
  initialFiles?: File[]
}

/** null = staged, not yet started. */
type StagedFileState = {
  file: File
  status: UploadStatus | null
  percent: number
  error?: string
}

const StagedFileStatusIcon = ({ staged }: { staged: StagedFileState }) => {
  switch (staged.status) {
    case UploadStatus.UPLOADING:
      return (
        <Flex gap={6} style={{ alignItems: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 12 }}>{staged.percent}%</span>
          <LoadingOutlined />
        </Flex>
      )
    case UploadStatus.DONE:
      return <CheckCircleFilled style={{ color: "#52c41a", flexShrink: 0 }} />
    case UploadStatus.ERROR:
      return (
        <Tooltip title={staged.error}>
          <CloseCircleFilled style={{ color: "#ff4d4f", flexShrink: 0 }} />
        </Tooltip>
      )
    default:
      return <PictureOutlined style={{ flexShrink: 0 }} />
  }
}

const ArtworkUploadModal = ({
  open,
  onClose,
  studentId,
  studentName,
  classes,
  initialClassId,
  initialFiles,
}: ArtworkUploadModalProps) => {
  const { message } = App.useApp()
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [stagedFiles, setStagedFiles] = useState<StagedFileState[]>([])
  const [selectedClassId, setSelectedClassId] = useState<Uuid | undefined>(
    initialClassId
  )
  const [selectedProjectId, setSelectedProjectId] = useState<Uuid | undefined>(
    undefined
  )
  const [artworkDate, setArtworkDate] = useState<Dayjs>(() => getNow())
  const [uploading, setUploading] = useState(false)
  // A manual pick must survive the classes subscription filling in late.
  const hasPickedClassRef = useRef(false)

  const sortedClasses = useMemo(() => sortClassesByRecency(classes), [classes])
  const defaultClassId = useMemo(
    () => initialClassId ?? getDefaultUploadClassId(sortedClasses),
    [initialClassId, sortedClasses]
  )

  // Fetch curriculum projects for the selected class
  const selectedClass = useMemo(
    () => sortedClasses.find((c) => c.id === selectedClassId),
    [sortedClasses, selectedClassId]
  )
  const curriculum = useOneDoc<CurriculumConstructor>(
    curriculumAtoms,
    DOC_TYPES.CURRICULUMS,
    selectedClass?.curriculumId ?? null
  )
  const curriculumProjectIds = useMemo(
    () => curriculum?.projectIds ?? [],
    [curriculum?.projectIds]
  )
  const curriculumProjects = useSomeDocs<ProjectConstructor>(
    projectAtoms,
    DOC_TYPES.PROJECTS,
    curriculumProjectIds
  )

  const projectOptions = useMemo(() => {
    if (!curriculumProjects) return []
    const projectsMap = new Map(curriculumProjects.map((p) => [p.id, p]))
    return curriculumProjectIds.map((id, index) => ({
      label: projectsMap.get(id)?.name ?? `Project ${index + 1}`,
      value: id,
    }))
  }, [curriculumProjectIds, curriculumProjects])

  // Reset per-open state (dropped files are staged fresh on every open).
  useEffect(() => {
    if (open) {
      hasPickedClassRef.current = false
      setSelectedProjectId(undefined)
      setArtworkDate(getNow())
      setStagedFiles(
        (initialFiles ?? []).map((file) => ({ file, status: null, percent: 0 }))
      )
    }
  }, [open, initialFiles])

  // Apply the default class on open and again when the classes subscription
  // fills in (the default can only be computed once classes exist) — but never
  // over a class the user picked by hand.
  useEffect(() => {
    if (!open || hasPickedClassRef.current) return
    setSelectedClassId(defaultClassId)
  }, [open, defaultClassId])

  // Reset project when class changes
  useEffect(() => {
    setSelectedProjectId(undefined)
  }, [selectedClassId])

  const classOptions = map(sortedClasses, (c) => ({
    label: c.name,
    value: c.id,
  }))

  const writeRenditions = async (artwork: StudentArtwork, source: Blob) => {
    const storage = Firestore.getInstance()
    if (!isRenditionWritable(artwork, storage.getAppBucket())) return
    const objectPath = parseStoragePathFromUrl(artwork.url)
    if (!objectPath) return

    try {
      await uploadArtworkRenditions(source, objectPath, {
        studentId,
        classId: artwork.classId,
        artworkId: artwork.id,
      })
      invalidateRenditionUrl(getRenditionPath(objectPath, RENDITION_TIER.THUMB))
      invalidateRenditionUrl(getRenditionPath(objectPath, RENDITION_TIER.MED))
    } catch (error) {
      Sentry.captureException(error)
      void message.warning(
        `${artwork.filename} saved, but its preview sizes could not be generated.`
      )
    }
  }

  /**
   * Shared upload core for both entry paths (the Dragger's customRequest and
   * the staged drag-and-drop list): Storage upload → `artwork.<id>` field-path
   * write → renditions (never blocking — writeRenditions catches internally).
   */
  const uploadArtworkFile = async (
    rawFile: File,
    onProgress?: (percent: number) => void
  ): Promise<string> => {
    invariant(
      selectedClassId != null,
      `No class selected for artwork upload for _student_id ${studentId}!`
    )

    const artworkId = createArtworkFileId() as Uuid
    let convertedFile: File
    try {
      convertedFile = await convertHeicToJpeg(rawFile)
    } catch (rejection: unknown) {
      reportHeicConversionFailure(rejection)
      throw new HeicConversionFailure(rejection)
    }
    const filename = buildArtworkFileName(
      artworkId,
      convertedFile.name.split(".").pop() ?? "jpg"
    )
    const path = `${studentId}/artwork/${filename}`

    const metadata = {
      studentId,
      classId: selectedClassId,
      artworkId,
      contentType: convertedFile.type,
    }

    const handleProgress = (snapshot: UploadTaskSnapshot) => {
      const percent = Math.round(
        (snapshot.bytesTransferred / snapshot.totalBytes) * 100
      )
      onProgress?.(percent)
    }

    const url = await new Promise<string>((resolve, reject) => {
      // The .catch routes synchronous setup failures (e.g. a bad ref) into
      // reject; without it this promise could stay pending forever.
      Firestore.getInstance()
        .uploadFile(
          convertedFile,
          path,
          metadata,
          handleProgress,
          resolve,
          reject
        )
        .catch((error: unknown) =>
          reject(
            error instanceof Error
              ? error
              : new Error("Artwork upload could not start")
          )
        )
    })

    const { timeCreated, semester } = getArtworkDateFields(
      artworkDate,
      getNow(),
      Date.now()
    )

    const artworkData: StudentArtwork = {
      id: artworkId,
      semester,
      classId: selectedClassId,
      inPortfolio: selectedClass?.group === CLASS_GROUPS.PORTFOLIO,
      ...(selectedProjectId ? { projectId: selectedProjectId } : {}),
      filename,
      path,
      timeCreated,
      url,
      rotation: 0,
    }

    await Firebase.getInstance().updateDocument(DOC_TYPES.STUDENTS, studentId, {
      [`artwork.${artworkId}`]: artworkData,
    })

    // Renditions come after the record write so a failure here can never
    // block the artwork itself; the wall falls back to the original.
    await writeRenditions(artworkData, convertedFile)

    return url
  }

  const customRequest: UploadProps["customRequest"] = async ({
    file,
    onSuccess,
    onError,
    onProgress,
  }) => {
    if (!selectedClassId) {
      onError?.(new Error("Please select a class first"))
      return
    }

    try {
      const url = await uploadArtworkFile(file as File, (percent) =>
        onProgress?.({ percent })
      )
      onSuccess?.(url, file)
    } catch (err: unknown) {
      const uploadError = isHeicConversionFailure(err)
        ? err
        : err instanceof Error
          ? err
          : new Error(ARTWORK_UPLOAD_PROCESSING_ERROR)

      onError?.(uploadError)
      message.error(artworkUploadFailureMessage(err))
    }
  }

  const handleUploadStaged = async () => {
    if (!selectedClassId) return
    setUploading(true)

    let failures = 0
    for (let index = 0; index < stagedFiles.length; index++) {
      const staged = stagedFiles[index]
      // A retry after a partial failure skips the files that already landed.
      if (staged.status === UploadStatus.DONE) continue

      const updateStaged = (patch: Partial<StagedFileState>) =>
        setStagedFiles((prev) =>
          prev.map((item, i) => (i === index ? { ...item, ...patch } : item))
        )

      updateStaged({
        status: UploadStatus.UPLOADING,
        percent: 0,
        error: undefined,
      })

      try {
        await uploadArtworkFile(staged.file, (percent) =>
          updateStaged({ percent })
        )
        updateStaged({ status: UploadStatus.DONE, percent: 100 })
      } catch (err: unknown) {
        failures += 1
        const uploadError = isHeicConversionFailure(err)
          ? err
          : err instanceof Error
            ? err
            : new Error(ARTWORK_UPLOAD_PROCESSING_ERROR)
        updateStaged({
          status: UploadStatus.ERROR,
          error: isHeicConversionFailure(err)
            ? ARTWORK_UPLOAD_PROCESSING_ERROR
            : uploadError.message,
        })
      }
    }

    setUploading(false)

    if (failures === 0) {
      void message.success(
        stagedFiles.length === 1
          ? "Artwork uploaded successfully."
          : `${stagedFiles.length} files uploaded successfully.`
      )
      handleClose()
    } else {
      void message.error(
        `${failures} of ${stagedFiles.length} uploads failed. Retry to finish.`
      )
    }
  }

  const handleChange = (info: UploadChangeParam) => {
    const { status } = info.file
    setFileList(info.fileList)

    const hasUploading = info.fileList.some(
      (f) => f.status === UploadStatus.UPLOADING
    )
    setUploading(hasUploading)

    if (status === UploadStatus.DONE) {
      message.success(`${info.file.name} uploaded successfully.`)
    } else {
      const filenameToast = filenameUploadFailedToast(
        status,
        info.file.error,
        info.file.name
      )
      if (filenameToast) message.error(filenameToast)
    }
  }

  const handleClose = () => {
    // The Modal X, mask click, and Esc land here too — never dismiss while
    // uploads are in flight, or a "cancelled" drop would still write artwork.
    if (uploading) return

    setFileList([])
    setStagedFiles([])
    hasPickedClassRef.current = false
    setSelectedClassId(defaultClassId)
    setSelectedProjectId(undefined)
    onClose()
  }

  const allUploaded =
    fileList.length > 0 && fileList.every((f) => f.status === UploadStatus.DONE)

  const isStagedMode = stagedFiles.length > 0
  const pendingCount = stagedFiles.filter(
    (staged) => staged.status !== UploadStatus.DONE
  ).length
  const hasFailures = stagedFiles.some(
    (staged) => staged.status === UploadStatus.ERROR
  )

  return (
    <Modal
      title={studentName ? `Upload Artwork — ${studentName}` : "Upload Artwork"}
      open={open}
      onCancel={handleClose}
      closable={!uploading}
      maskClosable={!uploading}
      keyboard={!uploading}
      footer={
        isStagedMode
          ? [
              <Button key="cancel" onClick={handleClose} disabled={uploading}>
                Cancel
              </Button>,
              <Button
                key="upload"
                type="primary"
                onClick={handleUploadStaged}
                loading={uploading}
                disabled={!selectedClassId}
                data-testid="artwork-upload-confirm"
              >
                {hasFailures
                  ? "Retry failed uploads"
                  : `Upload ${pendingCount} ${pendingCount === 1 ? "file" : "files"}`}
              </Button>,
            ]
          : null
      }
      width={500}
      destroyOnClose
    >
      {/* Plain div for the testid: Flex does not forward data-* props. */}
      <div data-testid="artwork-upload-modal">
        <Flex col gap={20} style={{ padding: "16px 0" }}>
          <Flex col gap={8}>
            <label style={{ fontWeight: 500 }}>Class</label>
            <Select
              placeholder="Select a class"
              value={selectedClassId}
              onChange={(value) => {
                hasPickedClassRef.current = true
                setSelectedClassId(value)
              }}
              options={classOptions}
              style={{ width: "100%" }}
              disabled={uploading}
              data-testid="artwork-upload-class"
            />
          </Flex>

          {projectOptions.length > 0 && (
            <Flex col gap={8}>
              <label style={{ fontWeight: 500 }}>Project (optional)</label>
              <Select
                placeholder="Select a project"
                value={selectedProjectId}
                onChange={setSelectedProjectId}
                options={projectOptions}
                allowClear
                style={{ width: "100%" }}
                disabled={uploading || !selectedClassId}
              />
            </Flex>
          )}

          <Flex col gap={8}>
            <label style={{ fontWeight: 500 }}>Artwork date</label>
            <div data-testid="artwork-upload-date">
              <DatePicker
                value={artworkDate}
                onChange={(date) => {
                  if (date) setArtworkDate(date)
                }}
                format={STANDARD_DATE_FORMAT}
                allowClear={false}
                disabledDate={(date) => date.isAfter(getNow(), "day")}
                style={{ width: "100%" }}
                disabled={uploading}
              />
            </div>
          </Flex>

          {isStagedMode ? (
            // Plain divs for the testids: Flex does not forward data-* props.
            <div
              data-testid="artwork-upload-staged-list"
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
            >
              {stagedFiles.map((staged, index) => (
                <div
                  key={`${staged.file.name}-${index}`}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "6px 10px",
                    border: "1px solid #d9d9d9",
                    borderRadius: 6,
                  }}
                  data-testid="artwork-upload-staged-file"
                  data-status={staged.status ?? "staged"}
                >
                  <TextOverrun text={staged.file.name} />
                  <StagedFileStatusIcon staged={staged} />
                </div>
              ))}
            </div>
          ) : (
            <Upload.Dragger
              multiple
              customRequest={customRequest}
              onChange={handleChange}
              fileList={fileList}
              disabled={!selectedClassId}
              accept="image/*,.heic,.heif"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag images to upload</p>
              <p className="ant-upload-hint">Supports multiple files</p>
            </Upload.Dragger>
          )}

          {allUploaded && (
            <Flex center>
              <span style={{ color: "#52c41a" }}>
                All files uploaded successfully!
              </span>
            </Flex>
          )}
        </Flex>
      </div>
    </Modal>
  )
}

export default ArtworkUploadModal
